import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTournamentAuth } from "../../utils/authorization";
import { useEventPayouts } from "../../hooks/useEventPayouts";
import { useTournamentVerification } from "../../hooks/useTournamentVerification";
import PayoutActionButtons from "./PayoutActionButtons";
import EventPayoutCard from "./EventPayoutCard";
import { SkeletonRows, formatCurrency } from "../UI/DesignStandards";
import { lockedPayoutTotal } from "../../utility/directorMoney";

const EventPayoutConfirmation = ({ tournamentId, tournamentEvents }) => {
  const [editMode, setEditMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const { user } = useAuth();

  // Check tournament authorization
  const { canManage } = useTournamentAuth(tournamentId);

  // Use the new verification system
  const {
    verificationStatus,
    loading: verificationLoading,
    refetch: refetchVerification,
  } = useTournamentVerification(tournamentId);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [isPayoutConfirmationOpen, setIsPayoutConfirmationOpen] =
    useState(false);
  const [originalPlacesToPay, setOriginalPlacesToPay] = useState({});
  const [localEventDetails, setLocalEventDetails] = useState({});

  // Get verification status from the new system
  const isFinalized = verificationStatus?.payouts?.isVerified || false;
  const isResultsFinalized = verificationStatus?.results?.isVerified || false;

  // Check if all teams have verified scores (required for payout finalization)
  const allTeamsVerified = useMemo(() => {
    if (
      !verificationStatus?.scores ||
      !Array.isArray(verificationStatus.scores)
    ) {
      return false;
    }

    // Get all team IDs that have scores (these represent teams in the tournament)
    const allTeamIds = verificationStatus.scores.map((vs) => vs.entityId);

    // Get verified team IDs
    const verifiedTeamIds = verificationStatus.scores
      .filter((vs) => vs.isVerified)
      .map((vs) => vs.entityId);

    // Check if ALL teams have verified scores
    const allVerified = allTeamIds.every((teamId) =>
      verifiedTeamIds.includes(teamId)
    );

    return allVerified;
  }, [verificationStatus?.scores]);

  // Get all event IDs
  const eventIds = tournamentEvents.map((event) => event.id);

  // Use a single hook for all events
  const { eventDetails, loading, error, refreshEventDetails } = useEventPayouts(
    eventIds,
    tournamentId
  );

  // Get the first event's details to access tournament-level data
  const firstEventDetails = eventDetails[eventIds[0]];

  // Store original placesToPay values when component mounts
  useEffect(() => {
    if (eventDetails) {
      const originalValues = {};
      Object.entries(eventDetails).forEach(([eventId, details]) => {
        originalValues[eventId] = details.placesToPay;
      });
      setOriginalPlacesToPay(originalValues);
    }
  }, [eventDetails]);

  // Initialize local event details when eventDetails changes.
  // Calculated places are already whole dollars or cents from the payout table.
  // Saved rows stay as stored, including manual edits.
  useEffect(() => {
    if (eventDetails) {
      setLocalEventDetails(eventDetails);
    }
  }, [eventDetails]);

  if (loading) {
    return (
      <div className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Event payouts
          </h2>
          <p className="text-sm text-brand-mist mt-1">
            Places paid and prize amounts for each event.
          </p>
        </div>
        <div className="p-8">
          <SkeletonRows count={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-board p-8 text-center">
        <h3 className="heading-display text-lg mb-2">
          Could not load event payouts
        </h3>
        <p className="text-brand-muted">Error loading payout information</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-brand-charcoal text-brand-cream rounded-lg shadow-sm px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Event payouts
          </h2>
          <p className="text-sm text-brand-mist mt-1">
            Places paid and prize amounts for each event.
            {firstEventDetails?.hasPayoutTable
              ? firstEventDetails.roundToWholeDollars === false
                ? " This payout table keeps cents."
                : " New place amounts from this payout table round to whole dollars."
              : ""}
          </p>
          {isFinalized ? (
            <p className="text-sm text-brand-cream mt-2">
              Locked payouts{" "}
              {formatCurrency(
                lockedPayoutTotal(
                  Object.keys(localEventDetails || {}).length
                    ? localEventDetails
                    : eventDetails
                )
              )}
            </p>
          ) : null}
        </div>
        {canManage && (
          <PayoutActionButtons
            editMode={editMode}
            setEditMode={setEditMode}
            verificationLoading={verificationLoading}
            isFinalized={isFinalized}
            isResultsFinalized={isResultsFinalized}
            allTeamsVerified={allTeamsVerified}
            localEventDetails={localEventDetails}
            unsavedChanges={unsavedChanges}
            setUnsavedChanges={setUnsavedChanges}
            originalPlacesToPay={originalPlacesToPay}
            setLocalEventDetails={setLocalEventDetails}
            refreshEventDetails={refreshEventDetails}
            refetchVerification={refetchVerification}
            tournamentId={tournamentId}
            user={user}
            firstEventDetails={firstEventDetails}
          />
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tournamentEvents
          .sort((a, b) => {
            if (a.isPrimary && !b.isPrimary) return -1;
            if (!a.isPrimary && b.isPrimary) return 1;
            return a.eventName.localeCompare(b.eventName);
          })
          .map((event) => {
            const details = localEventDetails[event.id];
            if (!details) return null;

            return (
              <EventPayoutCard
                key={event.id}
                event={event}
                details={details}
                editMode={editMode}
                localEventDetails={localEventDetails}
                setLocalEventDetails={setLocalEventDetails}
                unsavedChanges={unsavedChanges}
                setUnsavedChanges={setUnsavedChanges}
                user={user}
                firstEventDetails={firstEventDetails}
                refreshEventDetails={refreshEventDetails}
              />
            );
          })}
      </div>
    </div>
  );
};

export default EventPayoutConfirmation;
